import type {
  StartChildren,
  UserSummary,
  WorkflowGateMode,
  WorkflowStatus,
  WorkflowView,
} from '@/lib/api/generated/model';

/**
 * The builder's editing draft — the loaded document plus every unsaved edit.
 *
 * The draft is the single source of truth while editing. It is never rebuilt
 * from what the canvas renders: the canvas is a projection of the draft, and a
 * round trip through pixels would lose everything the canvas does not draw.
 *
 * That last point is the reason `loaded` is held verbatim. A workflow carries
 * fields this builder has no UI for — `inputs` today, more later — and a save
 * submits the *whole* document. Anything not carried across is deleted. See
 * `toRequest.ts`, where the carrying is done through a named allowlist rather
 * than a spread.
 */
export type WorkflowDraft = {
  /**
   * Everything the server returned, untouched. The passthrough source.
   *
   * Also the version anchor: `version` below starts as `loaded.version` and is
   * what a save submits as `expected_version`.
   */
  readonly loaded: WorkflowView;

  name: string;
  description: string | null;
  status: WorkflowStatus;
  version: number;

  /**
   * The workflow-wide execution-image override (spec 091). `null` = "use
   * deployment default". Meaningful for every AI step that does not set its
   * own `image` (`DraftStep.image` below), resolved by
   * `resolveEffectiveImageSource`.
   */
  image: string | null;

  /**
   * Ordered; the index is display order, which becomes the board's column
   * order. May include the reserved `$unassigned` phase.
   */
  phases: DraftPhase[];

  /**
   * The declared starting step id, or null while none is chosen.
   *
   * Declared rather than inferred because once a workflow loops, "the step
   * nothing targets" can be empty or ambiguous — the service settled this and
   * the builder has to send an answer (service research D17).
   */
  start: string | null;

  /**
   * Node offsets *relative to the band that owns them*, keyed by step id and by
   * the two system node ids.
   *
   * Band-relative rather than absolute so that reordering or resizing a phase
   * moves its steps with it instead of stranding them. Unwrapped from the
   * service's `{ nodes: … }` envelope on load and re-wrapped on save.
   */
  layout: Record<NodeId, Point>;

  /** Whether anything has changed since load or since the last successful save. */
  dirty: boolean;

  /**
   * The published workflow's pending changes this draft was loaded from or
   * last saved as (spec 144), or `null` when there are none — always `null`
   * for a workflow never published, whose draft is the workflow itself.
   *
   * Held beside `version` because the two are the anchors a save quotes: the
   * revision says which pending changes the author was looking at, the version
   * which published definition they started from.
   */
  pending: PendingDraft | null;
};

/** What the builder keeps of a published workflow's pending changes. */
export type PendingDraft = {
  /** Quoted by the next save, publish or discard, so a stale copy is refused. */
  revision: string;
  savedBy: UserSummary | null;
  /** The AI tool they saved through, as it reported itself (spec 246). */
  savedVia: string | null;
  savedAt: string;
  /** The live workflow changed since these changes were started. */
  outdated: boolean;
};

/**
 * The reserved phase holding steps the user has dropped on the canvas but not
 * yet assigned anywhere.
 *
 * A phase rather than a sibling list. The service reversed its own design here
 * once `phase_id IS NULL` turned out to be load-bearing on its side, and the
 * result is simpler for the builder too: deleting a phase moves its members
 * here rather than into a special-cased array, and every walk over
 * `draft.phases` covers unassigned steps for free.
 *
 * A draft may hold steps here; publishing may not (service V-16).
 */
export const UNASSIGNED_PHASE_ID = '$unassigned';

/** The synthesized nodes. Drawn on the canvas, never declared as steps. */
export const START_NODE_ID = '$start';
export const DONE_NODE_ID = '$done';

export type DraftPhase = {
  id: string;
  /**
   * Whether the id has been set deliberately and must stop following the name.
   *
   * Always true for `$unassigned`, whose id is reserved, and true for every
   * identifier of a published workflow, whose ids are load-bearing for tasks.
   */
  idPinned: boolean;
  name: string;
  /** Ordered; the index is display order within the phase. */
  steps: DraftStep[];
};

export type DraftStep = {
  id: string;
  idPinned: boolean;
  name: string;
  type: DraftStepType;
  prompt: string;
  llm: string | null;
  /**
   * This step's own execution-image override (spec 091). `null` = "inherit
   * from workflow". Meaningful only for `type === 'ai_step'`; kept on the
   * type for every step, mirroring how `llm` is typed, and gated in the UI
   * and on save the same way.
   */
  image: string | null;
  /** Human gates only; dropped when a step becomes an AI step. */
  mode: WorkflowGateMode | null;
  /**
   * Who this gate routes to (spec 067 FR-025). Human gates only, and dropped
   * when a step becomes an AI step — the same rule `mode` follows, and the same
   * one the database CHECK enforces.
   *
   * Held as id *and* the person, because the two are wanted in different places:
   * the id is what gets saved, and the name is what the panel and the export need
   * without a second lookup. `assignee` is null when the id resolves to nobody —
   * a definition imported from elsewhere naming someone this environment lacks.
   */
  assigneeUserId: string | null;
  assignee: UserSummary | null;
  /**
   * The target workflow's key (spec 097). Linked-workflow steps only; dropped
   * when a step becomes an AI step or human gate, the same rule `mode` and
   * `assigneeUserId` follow for their own step type.
   */
  workflow: string | null;
  /**
   * Whether the tasks a linked-workflow step creates start on their own
   * (`auto`) or wait for a person (`manual`), spec 140 US4. `null` or absent
   * leaves it to the service's default, `auto`; linked-workflow steps only.
   */
  startChildren?: StartChildren | null;
  /**
   * Extra guidance for the scheduler that starts a linked step's tasks, at most
   * 4,000 characters (service spec 183). Linked-workflow steps only; `null` or
   * blank sends nothing.
   */
  schedulingInstructions?: string | null;
  transitions: DraftTransition[];
};

/**
 * The three types a client may declare. `backlog` and `done` exist in the API's
 * step type union but are synthesized by the service and rejected on submission,
 * so they are deliberately absent here rather than filtered out later.
 */
export type DraftStepType = 'ai_step' | 'human_gate' | 'linked_workflow';

export type DraftTransition = {
  key: string;
  /** A step id, or `$done`. */
  to: string;
  when: string;
  label: string | null;
  traversalLimit: number | null;
};

/** A step id, or one of the two system node ids. */
export type NodeId = string;

export type Point = { x: number; y: number };
